// ─────────────────────────────────────────────────────────────────────────────
// TelemetryCard — live audit-event feed for the desktop homepage left panel
// (Chunk 5). DOM/CSS port of mobile/rexy-ios/src/components/home/TelemetryFeed.tsx.
//
// Purely presentational: props in, no socket. The desktop hook surfaces only the
// latest rexy:telemetry entry; RexyHomepage (Step 5) accumulates the rolling window
// (30 on desktop, spec §2.4 "wider") and assigns each entry a stable `id`, passed
// here so rows keep identity across prepends (no re-fade, no key collisions).
//
// Mirrors iOS badge styling via REXY_CONSOLE tokens. Desktop specifics per spec §2.4:
//   • new entries slide in from the top with a ~1s highlight fade
//   • SEC badge pulses bright red
//   • alternating row backgrounds #0A0A1A / #0D0D24
//   • PERSONAL/TRADING activity appears here — never filtered
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import { RexyTelemetry } from '../hooks/useRexySocket';

const MONO = REXY_FONT;

type TelemetryStatus = RexyTelemetry['status'];

// One feed row = a telemetry payload plus the client-assigned stable id.
export interface TelemetryFeedEntry extends RexyTelemetry {
  id: string;
}

// Keyframes for the SEC pulse and the new-row slide+highlight. Injected once with the
// card (single instance on the homepage) so the component stays self-contained.
const KEYFRAMES = `
@keyframes rexyTelemetrySecPulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
@keyframes rexyTelemetryRowIn {
  0% { opacity: 0; transform: translateY(-6px); background-color: rgba(0,191,255,0.10); }
  100% { opacity: 1; transform: translateY(0); background-color: transparent; }
}`;

// Badge fg/bg via REXY_CONSOLE tokens (mirrors iOS BADGE map).
const BADGE: Record<TelemetryStatus, { bg: string; fg: string }> = {
  OK: { bg: REXY_CONSOLE.badgeOkBg, fg: REXY_CONSOLE.primary },
  INF: { bg: REXY_CONSOLE.badgeInfBg, fg: REXY_CONSOLE.infText },
  WRN: { bg: REXY_CONSOLE.badgeWrnBg, fg: REXY_CONSOLE.warning },
  ERR: { bg: REXY_CONSOLE.badgeErrBg, fg: REXY_CONSOLE.critical },
  SEC: { bg: REXY_CONSOLE.badgeSecBg, fg: REXY_CONSOLE.critical },
};

function StatusBadge({ status }: { status: TelemetryStatus }) {
  const b = BADGE[status];
  return (
    <span
      style={{
        ...styles.badge,
        backgroundColor: b.bg,
        borderColor: b.fg,
        color: b.fg,
        // SEC pulses bright red; all others static.
        animation: status === 'SEC' ? 'rexyTelemetrySecPulse 1.2s ease-in-out infinite' : undefined,
      }}
    >
      {status}
    </span>
  );
}

function TelemetryRow({ entry, alt }: { entry: TelemetryFeedEntry; alt: boolean }) {
  return (
    <div
      style={{
        ...styles.row,
        // Alternating row backgrounds (spec §2.4).
        backgroundColor: alt ? REXY_CONSOLE.panel : REXY_CONSOLE.bg,
      }}
    >
      <span style={styles.time}>{entry.time}</span>
      <StatusBadge status={entry.status} />
      <span style={styles.event} title={entry.event}>
        {entry.event}
      </span>
    </div>
  );
}

export interface TelemetryCardProps {
  telemetry: TelemetryFeedEntry[]; // newest first; RexyHomepage caps the window at 30
  // Optional header override — the 6.7 Dashboard reuses this exact live feed as the
  // "║ ORCHESTRATION LOG" panel. Defaults to the homepage's "║ TELEMETRY".
  title?: string;
}

export default function TelemetryCard({ telemetry, title = '║ TELEMETRY' }: TelemetryCardProps) {
  return (
    <div style={styles.card}>
      <style>{KEYFRAMES}</style>
      <div style={styles.sectionHeader}>{title}</div>
      <div style={styles.list}>
        {telemetry.map((e, i) => (
          // Keyed by stable id → existing rows keep identity (no re-fade on prepend).
          <TelemetryRow key={e.id} entry={e} alt={i % 2 === 1} />
        ))}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    // Dimensional, never flat (CLAUDE.md UI §3).
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 10,
  },
  list: {
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '4px 6px',
    borderBottom: `1px solid ${REXY_CONSOLE.rowDivider}`,
    // Each newly-mounted row (a prepend) slides in + highlight-fades over ~1s.
    animation: 'rexyTelemetryRowIn 1s ease-out',
  },
  time: {
    fontFamily: MONO,
    fontSize: 10,
    color: REXY_CONSOLE.dimText,
    minWidth: 62,
  },
  badge: {
    fontFamily: MONO,
    fontSize: 10,
    letterSpacing: 1,
    border: '1px solid',
    borderRadius: 3,
    padding: '1px 5px',
    minWidth: 34,
    textAlign: 'center',
    display: 'inline-block',
  },
  event: {
    flex: 1,
    fontFamily: MONO,
    fontSize: 10,
    color: REXY_CONSOLE.infText,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 0,
  },
};
